import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAnalytics, fetchFocusStats, fetchCustomRangeAnalytics } from '../store/slices/analyticsSlice';
import { fetchAllProjects } from '../store/slices/projectsSlice';
import { AnalyticsType } from '../components/page-components/Dashboard/types';
import AlertModal from '../components/AlertModal';
import ProjectManagementModal from '../components/page-components/Dashboard/ProjectManagementModal';
import DownloadPDF from '../components/page-components/WorkLogAnalytics/DownloadPDF';
import { HiChevronDown } from 'react-icons/hi';
import { MdReceipt } from 'react-icons/md';


import { formatHours } from '../utils/helpers';
interface WorkLogAnalyticsProps {
  currentUser?: {
    id: string;
    name: string;
    email: string;
  };
}

const WorkLogAnalytics: React.FC<WorkLogAnalyticsProps> = ({ currentUser }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: analyticsData, stats, customRangeData, isLoading, isLoadingStats } = useAppSelector((state) => state.analytics);
  const { projects } = useAppSelector((state) => state.projects);

  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'year' | 'custom'>('week');
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [showProjectManagement, setShowProjectManagement] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [alertModal, setAlertModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    title: '',
    message: '',
    type: 'info',
  });

  // Fetch projects and sessions on mount
  useEffect(() => {
    dispatch(fetchAllProjects());
  }, [dispatch]);

  // Fetch analytics when period or project changes (skip fetch for custom until dates set)
  useEffect(() => {
    if (selectedPeriod === 'custom') return;
    const type = selectedPeriod === 'week'
      ? AnalyticsType.WEEKLY
      : selectedPeriod === 'month'
        ? AnalyticsType.MONTHLY
        : AnalyticsType.YEARLY;
    dispatch(fetchAnalytics({ type, projectId: selectedProjectId }));
  }, [dispatch, selectedPeriod, selectedProjectId]);

  // Fetch focus stats when project or period changes
  useEffect(() => {
    if (selectedPeriod === 'custom') return;
    const type = selectedPeriod === 'week'
      ? AnalyticsType.WEEKLY
      : selectedPeriod === 'month'
        ? AnalyticsType.MONTHLY
        : AnalyticsType.YEARLY;
    dispatch(fetchFocusStats({ type, projectId: selectedProjectId }));
  }, [dispatch, selectedPeriod, selectedProjectId]);

  // Fetch sessions for custom date range
  useEffect(() => {
    if (selectedPeriod === 'custom' && dateFrom && dateTo) {
      dispatch(fetchCustomRangeAnalytics({ dateFrom, dateTo, projectId: selectedProjectId }));
    }
  }, [dispatch, selectedPeriod, dateFrom, dateTo, selectedProjectId]);

  // Derive data from analyticsData
  const currentData = useMemo(() => {
    // Custom period: use customRangeData when dates are set
    if (selectedPeriod === 'custom') {
      if (customRangeData) {
        return {
          period: 'custom' as const,
          totalHours: customRangeData.totalHours,
          sessions: customRangeData.totalSessions,
          averageSession: customRangeData.averageSession,
          data: customRangeData.data,
        };
      }
      return {
        period: 'custom' as const,
        totalHours: 0,
        sessions: 0,
        averageSession: 0,
        data: [],
      };
    }
    const displayStats = stats || {
      totalHours: 0,
      avgHoursPerSession: 0,
      totalSessions: 0,
      streak: 0,
      productivity: 0
    };

    // Helper to group daily data into weeks for monthly view
    const getMonthlyGroupedData = () => {
      const calendar = analyticsData.monthly?.calendar || [];
      const weeks = [
        { week: 'Week 1', hours: 0, sessions: 0, productivity: 0 },
        { week: 'Week 2', hours: 0, sessions: 0, productivity: 0 },
        { week: 'Week 3', hours: 0, sessions: 0, productivity: 0 },
        { week: 'Week 4', hours: 0, sessions: 0, productivity: 0 }
      ];

      calendar.forEach((day) => {
        if (day.date <= 7) {
          weeks[0].hours += day.hours;
          weeks[0].sessions += day.sessions;
        } else if (day.date <= 14) {
          weeks[1].hours += day.hours;
          weeks[1].sessions += day.sessions;
        } else if (day.date <= 21) {
          weeks[2].hours += day.hours;
          weeks[2].sessions += day.sessions;
        } else {
          weeks[3].hours += day.hours;
          weeks[3].sessions += day.sessions;
        }
      });

      // Round to 1 decimal place removed to preserve precision for formatting
      // weeks.forEach(w => w.hours = Number(w.hours.toFixed(1)));
      return weeks;
    };

    return {
      period: selectedPeriod,
      totalHours: selectedPeriod === 'week'
        ? analyticsData.weekly?.days.reduce((acc, d) => acc + d.hours, 0) || 0
        : selectedPeriod === 'month'
          ? analyticsData.monthly?.totalHours || 0
          : analyticsData.yearly?.totalHours || 0,
      sessions: displayStats.totalSessions,
      averageSession: displayStats.avgHoursPerSession,
      productivity: displayStats.productivity,
      data: selectedPeriod === 'week'
        ? (analyticsData.weekly?.days || []).map(d => ({
          day: d.day,
          hours: d.hours,
          sessions: d.sessions,
          productivity: 0
        }))
        : selectedPeriod === 'month'
          ? getMonthlyGroupedData()
          : (analyticsData.yearly?.months || []).map(m => ({
            month: m.month,
            hours: m.hours,
            sessions: m.sessions,
            productivity: 0
          }))
    };
  }, [analyticsData, stats, selectedPeriod, customRangeData]);

  const selectedProject = selectedProjectId ? projects.find(p => p.id === selectedProjectId) : null;
  const displayTotalSessions = selectedPeriod === 'custom'
    ? (customRangeData?.totalSessions ?? 0)
    : (stats?.totalSessions || 0);

  const handleDownloadSuccess = () => {
    setAlertModal({
      open: true,
      title: 'Download Complete',
      message: `Work log downloaded as PDF from Focuzon!${selectedProject ? ` (${selectedProject.name})` : ''}`,
      type: 'success',
    });
  };

  const handleDownloadError = (error: any) => {
    setAlertModal({
      open: true,
      title: 'Download Failed',
      message: error?.message || 'Failed to download work log. Please try again.',
      type: 'error',
    });
  };

  return (
    // ... JSX

    <div className="min-h-screen bg-[#FFF8F0] relative overflow-hidden">
      {/* Background gradient shapes */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/3 w-72 h-72 bg-gradient-to-br from-orange-300/40 to-orange-200/30 rounded-full blur-3xl"></div>
      </div>

      {/* Main Content */}
      <main className={`max-w-7xl mx-auto px-6 py-8 relative z-10 transition-opacity duration-300 ${isLoading || isLoadingStats ? 'opacity-60' : 'opacity-100'}`}>
        {(isLoading || isLoadingStats) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md border border-orange-200/50 rounded-2xl px-6 py-3 shadow-xl shadow-orange-200/20">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-500 border-t-transparent"></div>
              <span className="text-base font-medium text-gray-800">Refining insights...</span>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Work Log Analytics</h2>
          <div className="flex flex-wrap items-center gap-3">
            {/* Project Filter */}
              <div className="relative w-48">
              <button
                onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                className="w-full h-12 px-4 bg-white border border-gray-200/60 rounded-xl text-left flex items-center justify-between hover:border-orange-300/50 transition-all"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {selectedProject ? (
                    <>
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: selectedProject.color || '#f97316' }}
                      />
                      <span className="text-gray-900 font-normal truncate">{selectedProject.name}</span>
                    </>
                  ) : (
                    <span className="text-gray-600 font-normal">All Projects</span>
                  )}
                </div>
                <HiChevronDown className={`text-gray-400 transition-transform ${isProjectDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isProjectDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsProjectDropdownOpen(false)}
                  />
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200/60 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setSelectedProjectId(undefined);
                          setIsProjectDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm rounded-lg flex items-center gap-2 transition-colors ${!selectedProjectId
                          ? 'bg-orange-50 text-orange-600'
                          : 'text-gray-700 hover:bg-orange-50/40'
                          }`}
                      >
                        <span className="w-3 h-3 rounded-full border-2 border-gray-300"></span>
                        <span>All Projects</span>
                      </button>
                      {projects.map((project) => (
                        <button
                          key={project.id}
                          onClick={() => {
                            setSelectedProjectId(project.id);
                            setIsProjectDropdownOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm rounded-lg flex items-center gap-2 transition-colors outline-none focus:outline-none focus:ring-0 ${selectedProjectId === project.id
                            ? 'bg-orange-50 text-orange-600'
                            : 'text-gray-700 hover:bg-orange-50/40'
                            }`}
                        >
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: project.color || '#f97316' }}
                          />
                          <span className="truncate flex-1"> {project.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

              <button
                onClick={() => navigate('/invoice', { state: { dateFrom: selectedPeriod === 'custom' ? (dateFrom || undefined) : undefined, dateTo: selectedPeriod === 'custom' ? (dateTo || undefined) : undefined } })}
                className="flex items-center gap-2 px-6 py-3 bg-white border border-orange-300/60 hover:bg-orange-50 text-orange-600 rounded-xl font-medium transition-all duration-300 hover:shadow-md"
              >
                <MdReceipt className="w-5 h-5" />
                <span>Generate Invoice</span>
              </button>
            <DownloadPDF
              period={selectedPeriod === 'custom' ? 'week' : selectedPeriod}
              project={selectedProject || undefined}
              currentData={currentData}
              totalSessions={displayTotalSessions}
              isLoading={isLoading}
              onSuccess={handleDownloadSuccess}
              onError={handleDownloadError}
              dateFrom={selectedPeriod === 'custom' ? (dateFrom || undefined) : undefined}
              dateTo={selectedPeriod === 'custom' ? (dateTo || undefined) : undefined}
            />
          </div>
        </div>

        {/* Period Selection */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-2 mb-8 border border-orange-200/50 shadow-lg">
          <div className="flex flex-col gap-3">
            <div className="flex space-x-2">
              {(['week', 'month', 'year', 'custom'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`flex-1 py-3 px-6 rounded-xl font-medium transition-all capitalize ${selectedPeriod === period
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-orange-50/50'
                    }`}
                >
                  {period}
                </button>
              ))}
            </div>
            {/* Custom date range - minimal, only when Custom is selected */}
            {selectedPeriod === 'custom' && (
              <div className="flex items-center gap-2 px-2 py-2 bg-white/60 rounded-lg border border-orange-100/80">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date range</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 px-2.5 bg-white border border-gray-200/80 rounded-lg text-sm focus:ring-1 focus:ring-orange-500/50 focus:border-orange-400 outline-none w-36"
                />
                <span className="text-gray-400 text-xs">→</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 px-2.5 bg-white border border-gray-200/80 rounded-lg text-sm focus:ring-1 focus:ring-orange-500/50 focus:border-orange-400 outline-none w-36"
                />
              </div>
            )}
          </div>
        </div>

        {/* Project Filter Info */}
        {/* {selectedProject && (
          <div className="mb-6 bg-orange-50/80 backdrop-blur-sm rounded-xl p-4 border border-orange-200/50">
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: selectedProject.color || '#f97316' }}
              />
              <div>
                <span className="text-sm text-gray-600">Showing analytics for: </span>
                <span className="text-sm font-semibold text-gray-900">{selectedProject.name}</span>
              </div>
            </div>
          </div>
        )} */}


        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-200/50 shadow-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {(isLoading || isLoadingStats) ? '...' : formatHours(currentData.totalHours)}
              </div>
              <div className="text-gray-600">Total Focus Time</div>
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-200/50 shadow-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {(isLoading || isLoadingStats) ? '...' : displayTotalSessions}
              </div>
              <div className="text-gray-600">Total Sessions</div>
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-200/50 shadow-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {(isLoading || isLoadingStats) ? '...' : formatHours(currentData.averageSession)}
              </div>
              <div className="text-gray-600">Avg Session Length</div>
            </div>
          </div>
        </div>

        {/* Focus Hours Chart */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-200/50 shadow-lg mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Focus Hours</h3>
          <div className="space-y-4">
            {(isLoading || isLoadingStats) ? (
              <div className="text-center py-8 text-gray-500">Loading analytics...</div>
            ) : currentData.data.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No data available for the selected period{selectedProject ? ` and project` : ''}.</div>
            ) : (
              currentData.data.map((item, index) => {
                const maxHours = Math.max(...currentData.data.map(d => d.hours), 1);
                const percentage = (item.hours / maxHours) * 100;
                return (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-20 text-sm text-gray-600 font-medium">
                      {'day' in item ? item.day : 'week' in item ? item.week : item.month}
                    </div>
                    <div className="flex-1 bg-orange-100/50 rounded-full h-6 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-16 text-sm text-gray-900 font-semibold text-right">
                      {formatHours(item.hours)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Detailed Data Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-200/50 shadow-lg">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Detailed Work Log</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-orange-200/50">
                  <th className="text-left py-4 text-gray-900 font-semibold">
                    {selectedPeriod === 'week' ? 'Day' : selectedPeriod === 'month' ? 'Week' : 'Month'}
                  </th>
                  <th className="text-center py-4 text-gray-900 font-semibold">Focus Hours</th>
                  <th className="text-center py-4 text-gray-900 font-semibold">Sessions</th>
                </tr>
              </thead>
              <tbody>
                {(isLoading || isLoadingStats) ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8 text-gray-500">Loading data...</td>
                  </tr>
                ) : currentData.data.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8 text-gray-500">No data available for the selected period{selectedProject ? ` and project` : ''}.</td>
                  </tr>
                ) : (
                  currentData.data.map((item, index) => (
                    <tr key={index} className="border-b border-orange-100/50">
                      <td className="py-4 text-gray-700">{'day' in item ? item.day : 'week' in item ? item.week : item.month}</td>
                      <td className="text-center py-4 text-gray-900">{formatHours(item.hours)}</td>
                      <td className="text-center py-4 text-gray-900">{item.sessions}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Insights */}
        {/* {!(isLoading || isLoadingStats) && currentData.data.length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-orange-50/80 to-orange-100/50 rounded-2xl p-6 border border-orange-200/50 shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">📊 Insights & Recommendations</h3>
            <div className="space-y-3">
              <p className="text-gray-700">
                • Your most productive time is <span className="text-orange-600 font-semibold">
                  {(() => {
                    const maxProd = Math.max(...currentData.data.map(d => d.productivity || 0));
                    const maxItem = currentData.data.find(d => (d.productivity || 0) === maxProd);
                    return maxItem ? ('day' in maxItem ? maxItem.day : 'week' in maxItem ? maxItem.week : maxItem.month) : 'N/A';
                  })()}
                </span> with {Math.max(...currentData.data.map(d => d.productivity || 0))}% productivity
              </p>
              <p className="text-gray-700">
                • You've completed <span className="text-green-600 font-semibold">{displayTotalSessions} focus sessions</span> this {selectedPeriod}
                {selectedProject && <span> for <span className="text-orange-600 font-semibold">{selectedProject.name}</span></span>}
              </p>
              <p className="text-gray-700">
                • Your average session length of <span className="text-orange-600 font-semibold">{currentData.averageSession.toFixed(1)} hours</span> is optimal for deep work
              </p>
              <p className="text-gray-700">
                • Consider scheduling more sessions during your peak productivity hours
              </p>
            </div>
          </div>
        )} */}
      </main>

      {/* Alert Modal */}
      <AlertModal
        open={alertModal.open}
        onClose={() => setAlertModal({ ...alertModal, open: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      {/* Project Management Modal */}
      <ProjectManagementModal
        isOpen={showProjectManagement}
        onClose={() => setShowProjectManagement(false)}
      />
    </div>
  );
};

export default WorkLogAnalytics;
